import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, Loader2, Store } from "lucide-react";
import { apiError, shopApi, useShop } from "./ShopContext";
import { PriceBlock, ProductImage, StockPill } from "./ProductCard";
import { QtyStepper } from "./CartDrawer";

export default function ShopProduct() {
  const { id } = useParams();
  const { addToCart } = useShop();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setP(null); setError("");
    shopApi.get(`/products/${id}`).then(({ data }) => setP(data)).catch((e) => setError(apiError(e, "Product not found")));
  }, [id]);

  if (error) return <p className="py-20 text-center text-slate-500" data-testid="shop-product-error">{error}</p>;
  if (!p) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#D96B27]" /></div>;

  return (
    <div data-testid="shop-product-page">
      <nav className="mb-4 flex items-center gap-1 text-xs text-slate-500">
        <Link to="/shop" className="hover:text-[#D96B27]">Home</Link><ChevronRight className="h-3 w-3" />
        {p.department && <><Link to={`/shop/products?department=${encodeURIComponent(p.department)}`} className="hover:text-[#D96B27]">{p.department}</Link><ChevronRight className="h-3 w-3" /></>}
        <span className="truncate text-slate-700">{p.name}</span>
      </nav>
      <div className="grid gap-8 rounded-2xl border border-slate-200 bg-white p-4 sm:p-8 md:grid-cols-2">
        <div className="aspect-square rounded-xl bg-slate-50 p-6"><ProductImage src={p.image_url} alt={p.name} /></div>
        <div>
          {p.category && <p className="text-xs font-semibold uppercase tracking-wider text-[#D96B27]">{p.category}</p>}
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="shop-product-name">{p.name}</h1>
          <p className="mt-1 text-xs text-slate-500">Code {p.plu_code}{p.barcode ? ` · ${p.barcode}` : ""}{p.unit ? ` · per ${p.unit}` : ""}</p>
          <div className="mt-5"><PriceBlock p={p} size="lg" /></div>
          <div className="mt-3"><StockPill p={p} /></div>
          {p.description && p.description !== p.name && <p className="mt-5 text-sm leading-relaxed text-slate-600">{p.description}</p>}
          {p.available > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <QtyStepper value={qty} max={p.available} onChange={(q) => setQty(Math.max(1, q))} testid="shop-product-qty" />
              <button type="button" onClick={() => addToCart(p, qty)} className="rounded-full bg-[#D96B27] px-6 py-3 font-bold text-white transition-colors hover:bg-[#C2591B]" data-testid="shop-product-add-btn">Add to cart</button>
              <button type="button" onClick={() => { addToCart(p, qty); navigate("/shop/checkout"); }} className="rounded-full border-2 border-[#1E293B] px-6 py-2.5 font-bold text-[#1E293B] hover:bg-slate-100" data-testid="shop-product-buy-btn">Buy now</button>
            </div>
          )}
          <div className="mt-8 flex items-start gap-3 rounded-xl bg-[#FEF3EC] p-4 text-sm text-slate-700">
            <Store className="mt-0.5 h-5 w-5 shrink-0 text-[#D96B27]" />
            <p><b>Collect in store.</b> We pack your order and let you know when it's ready. Pay at the counter with cash, card or your account.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
